import { all, select, takeEvery, fork, call, put } from 'redux-saga/effects'

// action
import { inquiryMgmtActions, configActions, routeActions } from '@redux/store/index'

// api
import { inquiryMgmtApi, usersApi, ResType } from '@api'

// lib
import { Logger } from '@utils'

// types
import { Inquiry, InquiryType, InquiryCommentType, InquiryStatusLogType } from '@schemas'
import { InquiryMgmtStateType } from '@redux/types'

const apiGetInquiries = async (status: string): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.getInquiries({
		query: { status },
	})
	return res
}

const apiGetInquiryById = async (inquiryId: string): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.getInquiryById({
		params: { inquiryId },
	})
	return res
}

const apiGetComments = async (inquiryId: string): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.getInquiryComments({
		params: { inquiryId },
	})
	return res
}

const apiGetStatusLog = async (inquiryId: string): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.getInquiryStatusLog({
		params: { inquiryId },
	})
	return res
}

const apiGetUsers = async (): Promise<ResType | undefined> => {
	const res: ResType | undefined = await usersApi.getUsers({
		query: { includingDeletedUsersYn: '0' },
	})
	return res
}

const apiUpdateStatus = async (
	inquiryId: string,
	body: { status: string; note: string },
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.updateInquiryStatus({
		params: { inquiryId },
		body,
	})
	return res
}

const apiUpdateHandler = async (
	inquiryId: string,
	body: { handlerId: string },
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.updateInquiryHandler({
		params: { inquiryId },
		body,
	})
	return res
}

const apiAddComment = async (
	inquiryId: string,
	body: { body: string; isInternal: boolean },
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.addInquiryComment({
		params: { inquiryId },
		body,
	})
	return res
}

const apiDeleteComment = async (
	inquiryId: string,
	commentId: string,
): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.deleteInquiryComment({
		params: { inquiryId, commentId },
	})
	return res
}

const apiDeleteInquiry = async (inquiryId: string): Promise<ResType | undefined> => {
	const res: ResType | undefined = await inquiryMgmtApi.deleteInquiry({
		params: { inquiryId },
	})
	return res
}

function* triggerInitInquiryMgmt() {
	try {
		yield put(inquiryMgmtActions.INQUIRY_LIST_STATUS('loading'))
		const statusFilter: string = yield select(state => state.inquiryMgmt.statusFilter)

		const [inquiryRes, usersRes]: [ResType, ResType] = yield all([
			call(apiGetInquiries, statusFilter),
			call(apiGetUsers),
		])

		if (inquiryRes && inquiryRes.status === 200) {
			const tempList = (inquiryRes.data as any[]) ?? []
			const newList = tempList.map(anItem => new Inquiry(anItem as InquiryType))
			yield put(inquiryMgmtActions.INQUIRY_LIST(newList))
			yield put(
				inquiryMgmtActions.INQUIRY_LIST_STATUS(newList.length > 0 ? 'ok' : 'empty'),
			)
		} else {
			yield put(inquiryMgmtActions.INQUIRY_LIST_STATUS('empty'))
		}

		if (usersRes && usersRes.status === 200) {
			const usersData = usersRes.data[0] as {
				users?: { id: string; name: string }[]
			}
			const users = usersData?.users ?? []
			yield put(
				inquiryMgmtActions.USER_LIST(users.map(u => ({ id: u.id, name: u.name }))),
			)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerLoadInquiryDetail() {
	try {
		yield put(inquiryMgmtActions.SELECTED_INQUIRY_STATUS('loading'))
		const selectedInquiry: InquiryType | null = yield select(
			state => state.inquiryMgmt.selectedInquiry,
		)
		if (!selectedInquiry) {
			yield put(inquiryMgmtActions.SELECTED_INQUIRY_STATUS('empty'))
			return
		}

		const userList: { id: string; name: string }[] = yield select(
			state => state.inquiryMgmt.userList,
		)

		const effects: any[] = [
			call(apiGetInquiryById, selectedInquiry.id),
			call(apiGetComments, selectedInquiry.id),
			call(apiGetStatusLog, selectedInquiry.id),
		]
		if (userList.length === 0) {
			effects.push(call(apiGetUsers))
		}

		const results: ResType[] = yield all(effects)
		const [detailRes, commentsRes, statusLogRes, usersRes] = results

		if (detailRes && detailRes.status === 200) {
			const list = (detailRes.data as any[]) ?? []
			const inquiryData = list[0] ? new Inquiry(list[0] as InquiryType) : null
			yield put(inquiryMgmtActions.SELECTED_INQUIRY(inquiryData))
			yield put(inquiryMgmtActions.SELECTED_INQUIRY_STATUS(inquiryData ? 'ok' : 'empty'))
		} else {
			yield put(inquiryMgmtActions.SELECTED_INQUIRY_STATUS('empty'))
		}

		if (commentsRes && commentsRes.status === 200) {
			const comments = (commentsRes.data as unknown as InquiryCommentType[]) ?? []
			yield put(inquiryMgmtActions.COMMENTS(comments))
		}

		if (statusLogRes && statusLogRes.status === 200) {
			const logs = (statusLogRes.data as unknown as InquiryStatusLogType[]) ?? []
			yield put(inquiryMgmtActions.STATUS_LOGS(logs))
		}

		if (usersRes && usersRes.status === 200) {
			const usersData = usersRes.data[0] as {
				users?: { id: string; name: string }[]
			}
			const users = usersData?.users ?? []
			yield put(
				inquiryMgmtActions.USER_LIST(users.map(u => ({ id: u.id, name: u.name }))),
			)
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerUpdateStatus() {
	try {
		const payload: InquiryMgmtStateType['updateStatusPayload'] = yield select(
			state => state.inquiryMgmt.updateStatusPayload,
		)
		const res: ResType = yield call(apiUpdateStatus, payload.inquiryId, {
			status: payload.status,
			note: payload.note,
		})
		if (res && (res.status === 200 || res.status === 201)) {
			yield put(configActions.TOAST({ isShow: true, message: '상태가 변경되었습니다.' }))
			yield put(inquiryMgmtActions.TRIGGER_LOAD_INQUIRY_DETAIL(true))
			yield put(inquiryMgmtActions.TRIGGER_INIT_INQUIRY_MGMT(true))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerUpdateHandler() {
	try {
		const payload: InquiryMgmtStateType['updateHandlerPayload'] = yield select(
			state => state.inquiryMgmt.updateHandlerPayload,
		)
		const res: ResType = yield call(apiUpdateHandler, payload.inquiryId, {
			handlerId: payload.handlerId,
		})
		if (res && (res.status === 200 || res.status === 201)) {
			yield put(
				configActions.TOAST({ isShow: true, message: '담당자가 변경되었습니다.' }),
			)
			yield put(inquiryMgmtActions.TRIGGER_LOAD_INQUIRY_DETAIL(true))
			yield put(inquiryMgmtActions.TRIGGER_INIT_INQUIRY_MGMT(true))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerAddComment() {
	try {
		const payload: InquiryMgmtStateType['addCommentPayload'] = yield select(
			state => state.inquiryMgmt.addCommentPayload,
		)
		const res: ResType = yield call(apiAddComment, payload.inquiryId, {
			body: payload.body,
			isInternal: payload.isInternal,
		})
		if (res && (res.status === 200 || res.status === 201)) {
			yield put(inquiryMgmtActions.TRIGGER_LOAD_INQUIRY_DETAIL(true))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerDeleteComment() {
	try {
		const payload: InquiryMgmtStateType['deleteCommentPayload'] = yield select(
			state => state.inquiryMgmt.deleteCommentPayload,
		)
		const res: ResType = yield call(
			apiDeleteComment,
			payload.inquiryId,
			payload.commentId,
		)
		if (res && (res.status === 200 || res.status === 201)) {
			yield put(inquiryMgmtActions.TRIGGER_LOAD_INQUIRY_DETAIL(true))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerDeleteInquiry() {
	try {
		const inquiryId: string = yield select(state => state.inquiryMgmt.deleteInquiryId)
		const res: ResType = yield call(apiDeleteInquiry, inquiryId)
		if (res && (res.status === 200 || res.status === 201)) {
			yield put(configActions.TOAST({ isShow: true, message: '문의가 삭제되었습니다.' }))
			yield put(routeActions.NAVIGATE_PATH('/main/inquiry'))
			yield put(inquiryMgmtActions.TRIGGER_INIT_INQUIRY_MGMT(true))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(inquiryMgmtActions.TRIGGER_INIT_INQUIRY_MGMT, triggerInitInquiryMgmt)
	yield takeEvery(
		inquiryMgmtActions.TRIGGER_LOAD_INQUIRY_DETAIL,
		triggerLoadInquiryDetail,
	)
	yield takeEvery(inquiryMgmtActions.TRIGGER_UPDATE_STATUS, triggerUpdateStatus)
	yield takeEvery(inquiryMgmtActions.TRIGGER_UPDATE_HANDLER, triggerUpdateHandler)
	yield takeEvery(inquiryMgmtActions.TRIGGER_ADD_COMMENT, triggerAddComment)
	yield takeEvery(inquiryMgmtActions.TRIGGER_DELETE_COMMENT, triggerDeleteComment)
	yield takeEvery(inquiryMgmtActions.TRIGGER_DELETE_INQUIRY, triggerDeleteInquiry)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
